import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { moveMail, deleteMail, copyMail, renameMail } from '@/services/api/endpoints';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import { reportError } from '@/services/errors/errorReporter';

// Invalidate every mail list + the folder tree. Broad on purpose: a mail can be
// foldered (['mails', folderId]) or at a project root (['mails', 'root', cid]),
// and move/copy touch two locations — invalidating the whole ['mails'] prefix
// covers all of them without threading ids/containers through every call site.
function invalidateMails(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ['mails'] });
  void qc.invalidateQueries({ queryKey: ['folders'] });
  // Et les résultats de recherche, qui affichent le libellé du mail et son
  // dossier : sans cela un mail renommé depuis la recherche resterait listé sous
  // son ancien nom, et un mail supprimé resterait cliquable (téléchargement en
  // 404). Cf. la note de `useFolderMutations.invalidateTree`.
  void qc.invalidateQueries({ queryKey: ['search'] });
}

function report(op: string) {
  return (err: Error) =>
    reportError({ error: err instanceof Error ? err : new Error(String(err)), tags: ['mail', op] });
}

export function useMoveMail() {
  const qc = useQueryClient();
  return useMutation<void, Error, { mailId: string; sourceFolderId: string | null; targetFolderId: string }>({
    mutationFn: ({ mailId, targetFolderId }) => {
      addBreadcrumb({ category: 'mutation', message: 'move-mail', data: { mailId, targetFolderId } });
      return moveMail({ mailId, targetFolderId });
    },
    onError: report('move'),
    onSettled: () => invalidateMails(qc),
  });
}

export function useCopyMail() {
  const qc = useQueryClient();
  return useMutation<
    { status: 'created'; mailId: string },
    Error,
    { mailId: string; sourceFolderId: string | null; targetFolderId: string }
  >({
    mutationFn: ({ mailId, targetFolderId }) => {
      addBreadcrumb({ category: 'mutation', message: 'copy-mail', data: { mailId, targetFolderId } });
      return copyMail({ mailId, targetFolderId });
    },
    onError: report('copy'),
    onSettled: () => invalidateMails(qc),
  });
}

export function useDeleteMail() {
  const qc = useQueryClient();
  return useMutation<void, Error, { mailId: string; folderId: string | null }>({
    mutationFn: ({ mailId }) => {
      addBreadcrumb({ category: 'mutation', message: 'delete-mail', data: { mailId } });
      return deleteMail(mailId);
    },
    onError: report('delete'),
    onSettled: () => invalidateMails(qc),
  });
}

export function useRenameMail() {
  const qc = useQueryClient();
  return useMutation<void, Error, { mailId: string; folderId: string | null; displayName: string }>({
    mutationFn: ({ mailId, displayName }) => {
      addBreadcrumb({ category: 'mutation', message: 'rename-mail', data: { mailId } });
      return renameMail({ mailId, displayName });
    },
    onError: report('rename'),
    onSettled: () => invalidateMails(qc),
  });
}
